import React, { useCallback, useEffect, useState } from 'react'
import { Box, Checkbox, Divider, MenuItem, Tooltip, Typography } from '@mui/material'
import dayjs, { type Dayjs } from 'dayjs'

import { accentColors, lightTokens, typography } from '~tokens/internal'

import Button from '../Button'
import Icon from '../Icon'
import Search from '../Search'
import TextField from '../TextField'
import InternalTypography from '../Typography'

import AnswerFilter, { type AnswerFilterEntry, createEmptyFilter } from './AnswerFilter'
import DateRangePanel from './DateRangePanel'
import type { FilterCategory, FilterDrilldown, FilterRefinement } from './Filters'
import { type FilterState, emptyFilterState } from './useFilterState'

const SEARCH_THRESHOLD = 10

interface DatePreset {
  id: string
  label: string
  from: Dayjs
  to: Dayjs
}

const buildDatePresets = (now: Dayjs): DatePreset[] => {
  const yesterday = now.subtract(1, 'day')
  return [
    { id: 'today', label: 'Today', from: now.startOf('day'), to: now.endOf('day') },
    {
      id: 'yesterday',
      label: 'Yesterday',
      from: yesterday.startOf('day'),
      to: yesterday.endOf('day'),
    },
    {
      id: 'past-7-days',
      label: 'Past 7 Days',
      from: now.subtract(6, 'day').startOf('day'),
      to: now.endOf('day'),
    },
    {
      id: 'this-month',
      label: 'This Month',
      from: now.startOf('month'),
      to: now.endOf('month'),
    },
  ]
}

/** Re-renders at each local midnight so date presets never go stale while open. */
const useCurrentDay = (enabled: boolean): Dayjs => {
  const [day, setDay] = useState(() => dayjs().startOf('day'))
  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setTimeout>
    const schedule = (): void => {
      const now = dayjs()
      timer = setTimeout(
        () => {
          setDay(dayjs().startOf('day'))
          schedule()
        },
        now.add(1, 'day').startOf('day').diff(now) + 1,
      )
    }
    schedule()
    return (): void => clearTimeout(timer)
  }, [enabled])
  return day
}

const formatPresetRange = (from: Dayjs, to: Dayjs): string => {
  if (from.isSame(to, 'day')) return from.format('MMM D, YYYY')
  if (from.isSame(to, 'year')) return `${from.format('MMM D')}\u2013${to.format('MMM D, YYYY')}`
  return `${from.format('MMM D, YYYY')}\u2013${to.format('MMM D, YYYY')}`
}

export interface FilterCategoryPanelProps {
  category: FilterCategory
  state: FilterState
  onChange: (state: FilterState) => void
  /**
   * Opens the panel directly to a specific refinement (used when refinements
   * are surfaced as top-level entries, e.g. a fixed workflow on the Process
   * page). When set, the "Back to all options" control is hidden.
   */
  initialRefinement?: { optionId: string; refinement: FilterRefinement }
}

const FilterCategoryPanel: React.FC<FilterCategoryPanelProps> = ({
  category,
  state,
  onChange,
  initialRefinement,
}) => {
  const {
    selections = {},
    dateRanges = {},
    refinements = {},
    answerFilters = {},
  } = state ?? emptyFilterState

  const [activeDrilldown, setActiveDrilldown] = useState<{
    drilldown: FilterDrilldown
  } | null>(null)
  const [activeRefinement, setActiveRefinement] = useState<{
    optionId: string
    refinement: FilterRefinement
  } | null>(initialRefinement ?? null)
  const [submenuSearch, setSubmenuSearch] = useState('')
  const [showCustomRange, setShowCustomRange] = useState(false)
  const currentDay = useCurrentDay(category.type === 'date')

  // When surfaced as a top-level entry, keep the panel pinned to the requested
  // refinement (and follow changes as the user hovers between sibling entries).
  useEffect(() => {
    if (initialRefinement) {
      setActiveRefinement(initialRefinement)
      setSubmenuSearch('')
    }
  }, [initialRefinement?.optionId, initialRefinement?.refinement.id])

  const isOptionSelected = (optionId: string): boolean =>
    selections[category.id]?.includes(optionId) ?? false

  const getAnswerFilterCount = (optionId: string): number =>
    (answerFilters[category.id]?.[optionId] ?? []).filter((f) => f.applied).length

  const getDrilldownCount = (drilldown: FilterDrilldown): number => {
    const selected = selections[category.id]
    if (!selected?.length) return 0
    return drilldown.options.filter((opt) => selected.includes(opt.id)).length
  }

  const handleOptionSelect = useCallback(
    (optionId: string) => {
      const current = selections[category.id] ?? []
      const isDeselecting = current.includes(optionId)
      const nextSelections = isDeselecting
        ? current.filter((id) => id !== optionId)
        : [...current, optionId]
      let nextRefinements = refinements
      let nextAnswerFilters = answerFilters
      if (isDeselecting) {
        if (refinements[category.id]?.[optionId]) {
          const catRef = { ...refinements[category.id] }
          delete catRef[optionId]
          nextRefinements = { ...refinements, [category.id]: catRef }
        }
        if (answerFilters[category.id]?.[optionId]) {
          const catAF = { ...answerFilters[category.id] }
          delete catAF[optionId]
          nextAnswerFilters = { ...answerFilters, [category.id]: catAF }
        }
      }
      onChange({
        ...(state ?? emptyFilterState),
        selections: { ...selections, [category.id]: nextSelections },
        refinements: nextRefinements,
        answerFilters: nextAnswerFilters,
      })
    },
    [state, onChange, selections, refinements, answerFilters, category.id],
  )

  const handleRefinementSelect = useCallback(
    (optionId: string, refinementId: string, valueId: string) => {
      const current = refinements[category.id]?.[optionId]?.[refinementId] ?? []
      const has = current.includes(valueId)
      const next = has ? current.filter((id) => id !== valueId) : [...current, valueId]
      onChange({
        ...(state ?? emptyFilterState),
        refinements: {
          ...refinements,
          [category.id]: {
            ...(refinements[category.id] ?? {}),
            [optionId]: {
              ...(refinements[category.id]?.[optionId] ?? {}),
              [refinementId]: next,
            },
          },
        },
      })
    },
    [state, onChange, refinements, category.id],
  )

  const renderRefinementMenuItem = (
    optionId: string,
    ref: FilterRefinement,
    indented: boolean,
  ): React.ReactNode => (
    <MenuItem
      key={ref.id}
      className={`filter-menu-item${indented ? ' filter-menu-item--indented' : ''}`}
      onClick={() => setActiveRefinement({ optionId, refinement: ref })}
    >
      <Box className='filter-item-content'>
        {ref.icon && (
          <Icon name={ref.icon} size={typography.fontSize.lg} color={lightTokens.text.secondary} />
        )}
        <Typography variant='body2' noWrap sx={{ color: 'text.secondary' }}>
          {ref.label}
        </Typography>
      </Box>
      {ref.options && (refinements[category.id]?.[optionId]?.[ref.id] ?? []).length > 0 && (
        <Box className='filter-badge'>
          {(refinements[category.id]?.[optionId]?.[ref.id] ?? []).length}
        </Box>
      )}
      {ref.formFields && getAnswerFilterCount(optionId) > 0 && (
        <Box className='filter-badge'>{getAnswerFilterCount(optionId)}</Box>
      )}
      <Icon name='ArrowForward' size={typography.fontSize.lg} color={lightTokens.text.secondary} />
    </MenuItem>
  )

  // Fixed (workflow-page) process category: render the implied workflow's
  // refinements at the top level instead of a selectable workflow option.
  const fixedOption =
    category.type === 'process' && category.fixedOptionId
      ? category.options?.find((o) => o.id === category.fixedOptionId)
      : undefined

  if (category.type === 'text') {
    return (
      <>
        <Typography
          variant='overline'
          className='filter-section-header'
          sx={{ lineHeight: '2', color: 'text.secondary' }}
        >
          {category.label}
        </Typography>
        <Box sx={{ px: 1, pb: 1 }} onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            placeholder='Filter by text'
            value={(state?.textValues ?? {})[category.id] ?? ''}
            onChange={(e) =>
              onChange({
                ...(state ?? emptyFilterState),
                textValues: { ...(state?.textValues ?? {}), [category.id]: e.target.value },
              })
            }
            fullWidth
            size='small'
          />
        </Box>
      </>
    )
  }

  if (category.type === 'date') {
    const currentRange = dateRanges[category.id]
    const isPresetActive = (preset: DatePreset): boolean =>
      Boolean(currentRange?.from?.isSame(preset.from) && currentRange?.to?.isSame(preset.to))
    const handleDateReset = (): void => {
      const s = state ?? emptyFilterState
      const nextDateRanges = { ...s.dateRanges }
      delete nextDateRanges[category.id]
      let nextSelections = s.selections
      if (category.overdueOption) {
        const current = s.selections?.[category.id] ?? []
        nextSelections = {
          ...s.selections,
          [category.id]: current.filter((id) => id !== category.overdueOption!.id),
        }
      }
      onChange({ ...s, selections: nextSelections, dateRanges: nextDateRanges })
    }
    const handleRangeChange = (from: Dayjs | null, to: Dayjs | null): void =>
      onChange({
        ...(state ?? emptyFilterState),
        dateRanges: { ...dateRanges, [category.id]: { from, to } },
      })

    if (showCustomRange) {
      return (
        <>
          <MenuItem
            className='filter-menu-item filter-menu-item--back'
            onClick={() => setShowCustomRange(false)}
          >
            <Icon
              name='arrowBack'
              size={typography.fontSize.lg}
              color={lightTokens.text.secondary}
            />
            <Typography variant='body2'>Back to {category.label}</Typography>
          </MenuItem>
          <Divider className='filter-divider' />
          <Typography
            variant='overline'
            className='filter-section-header'
            sx={{ color: 'text.secondary', lineHeight: '2' }}
          >
            Custom Range
          </Typography>
          <DateRangePanel
            startDate={currentRange?.from ?? null}
            endDate={currentRange?.to ?? null}
            onChange={handleRangeChange}
          />
          {category.showReset && (
            <>
              <Divider className='filter-divider' />
              <Button
                variant='text'
                fullWidth
                className='filter-reset-button'
                onClick={handleDateReset}
              >
                Reset Filter
              </Button>
            </>
          )}
        </>
      )
    }

    return (
      <>
        <Typography
          variant='overline'
          className='filter-section-header'
          sx={{
            color: 'text.secondary',
            lineHeight: '2',
          }}
        >
          {category.label}
        </Typography>
        {category.overdueOption && (
          <MenuItem
            className='filter-menu-item'
            onClick={() => handleOptionSelect(category.overdueOption!.id)}
          >
            <Box className='filter-item-content'>
              <Checkbox
                size='small'
                checked={isOptionSelected(category.overdueOption!.id)}
                className='filter-checkbox'
                tabIndex={-1}
                disableRipple
              />
              <Icon name='warning' size={typography.fontSize.lg} color={lightTokens.warning.main} />
              <Typography variant='body2' noWrap>
                {category.overdueOption.label}
              </Typography>
            </Box>
          </MenuItem>
        )}
        {buildDatePresets(currentDay).map((preset) => {
          const active = isPresetActive(preset)
          return (
            <React.Fragment key={preset.id}>
              <MenuItem
                className='filter-menu-item'
                onClick={() =>
                  active ? handleRangeChange(null, null) : handleRangeChange(preset.from, preset.to)
                }
              >
                <Box className='filter-item-content'>
                  <Checkbox
                    size='small'
                    checked={active}
                    className='filter-checkbox'
                    tabIndex={-1}
                    disableRipple
                  />
                  <Box className='filter-date-preset'>
                    <Typography variant='body2' color='textPrimary'>
                      {preset.label}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {formatPresetRange(preset.from, preset.to)}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <Divider className='filter-divider' />
            </React.Fragment>
          )
        })}
        <MenuItem onClick={() => setShowCustomRange(true)} className='filter-menu-item'>
          <Box className='filter-item-content'>
            <Typography variant='body2' color='textPrimary'>
              Custom Range
            </Typography>
          </Box>
          <Icon
            name='chevronRight'
            size={typography.fontSize.lg}
            color={lightTokens.text.secondary}
          />
        </MenuItem>
      </>
    )
  }

  if (activeDrilldown) {
    return (
      <>
        <MenuItem
          className='filter-menu-item filter-menu-item--back'
          onClick={() => {
            setActiveDrilldown(null)
            setSubmenuSearch('')
          }}
        >
          <Icon name='arrowBack' size={typography.fontSize.lg} color={lightTokens.text.secondary} />
          <Typography variant='body2'>Back to {category.label} options</Typography>
        </MenuItem>
        <Divider className='filter-divider' />
        {activeDrilldown.drilldown.options.map((opt) => (
          <MenuItem
            key={opt.id}
            className='filter-menu-item'
            onClick={() => handleOptionSelect(opt.id)}
          >
            <Box className='filter-item-content'>
              <Checkbox
                size='small'
                checked={isOptionSelected(opt.id)}
                className='filter-checkbox'
                tabIndex={-1}
                disableRipple
              />
              {opt.avatar}
              {opt.icon && (
                <Icon
                  name={opt.icon}
                  size={typography.fontSize.lg}
                  color={opt.iconColor ?? lightTokens.text.primary}
                />
              )}
              {opt.node ? (
                opt.node
              ) : (
                <Typography variant='body2' noWrap>
                  {opt.label}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
        {category.showReset && (
          <>
            <Divider className='filter-divider' />
            <Button
              variant='text'
              fullWidth
              className='filter-reset-button'
              onClick={() => {
                const s = state ?? emptyFilterState
                const current = s.selections?.[category.id] ?? []
                const drilldownIds = new Set(activeDrilldown.drilldown.options.map((o) => o.id))
                onChange({
                  ...s,
                  selections: {
                    ...s.selections,
                    [category.id]: current.filter((id) => !drilldownIds.has(id)),
                  },
                })
              }}
            >
              Reset Filter
            </Button>
          </>
        )}
      </>
    )
  }

  if (activeRefinement) {
    return (
      <>
        {!initialRefinement && (
          <>
            <MenuItem
              className='filter-menu-item filter-menu-item--back'
              onClick={() => {
                setActiveRefinement(null)
                setSubmenuSearch('')
              }}
            >
              <Icon
                name='arrowBack'
                size={typography.fontSize.lg}
                color={lightTokens.text.secondary}
              />
              <Typography variant='body2'>Back to All {category.label} options</Typography>
            </MenuItem>
            <Divider className='filter-divider' />
          </>
        )}
        <Box className='filter-refinement-info'>
          <Typography
            variant='overline'
            className='filter-refinement-title'
            sx={{
              lineHeight: '2',
              color: 'text.secondary',
            }}
          >
            {activeRefinement.refinement.label}
          </Typography>
          <Box className='filter-refinement-process'>
            <Icon name='sensors' color={accentColors.green.main} />
            <Typography variant='body1'>
              {category.options?.find((o) => o.id === activeRefinement.optionId)?.label}
            </Typography>
          </Box>
        </Box>
        {activeRefinement.refinement.options ? (
          <>
            {activeRefinement.refinement.options.map((opt) => (
              <MenuItem
                key={opt.id}
                className='filter-menu-item'
                onClick={() =>
                  handleRefinementSelect(
                    activeRefinement.optionId,
                    activeRefinement.refinement.id,
                    opt.id,
                  )
                }
              >
                <Box className='filter-item-content'>
                  <Checkbox
                    size='small'
                    checked={(
                      refinements[category.id]?.[activeRefinement.optionId]?.[
                        activeRefinement.refinement.id
                      ] ?? []
                    ).includes(opt.id)}
                    className='filter-checkbox'
                    tabIndex={-1}
                    disableRipple
                  />
                  <Typography variant='body2' noWrap>
                    {opt.label}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </>
        ) : activeRefinement.refinement.formFields ? (
          <Box className='filter-refinement-node'>
            <AnswerFilter
              fields={activeRefinement.refinement.formFields}
              value={
                answerFilters[category.id]?.[activeRefinement.optionId] ?? [createEmptyFilter()]
              }
              onChange={(updated: AnswerFilterEntry[]) => {
                onChange({
                  ...(state ?? emptyFilterState),
                  answerFilters: {
                    ...answerFilters,
                    [category.id]: {
                      ...(answerFilters[category.id] ?? {}),
                      [activeRefinement.optionId]: updated,
                    },
                  },
                })
              }}
            />
          </Box>
        ) : activeRefinement.refinement.node ? (
          <Box className='filter-refinement-node'>{activeRefinement.refinement.node}</Box>
        ) : null}
        {category.showReset && (
          <>
            <Divider className='filter-divider' />
            <Button
              variant='text'
              fullWidth
              className='filter-reset-button'
              onClick={() => {
                const s = state ?? emptyFilterState
                const catRef = { ...(s.refinements?.[category.id] ?? {}) }
                const optRef = { ...(catRef[activeRefinement.optionId] ?? {}) }
                delete optRef[activeRefinement.refinement.id]
                catRef[activeRefinement.optionId] = optRef
                const catAF = { ...(s.answerFilters?.[category.id] ?? {}) }
                delete catAF[activeRefinement.optionId]
                onChange({
                  ...s,
                  refinements: {
                    ...s.refinements,
                    [category.id]: catRef,
                  },
                  answerFilters: {
                    ...s.answerFilters,
                    [category.id]: catAF,
                  },
                })
              }}
            >
              Reset Filter
            </Button>
          </>
        )}
      </>
    )
  }

  if (fixedOption) {
    return (
      <>
        <Typography
          variant='overline'
          className='filter-section-header'
          sx={{ lineHeight: '2', color: 'text.secondary' }}
        >
          {category.label}
        </Typography>
        {(fixedOption.refinements ?? []).map((ref) =>
          renderRefinementMenuItem(fixedOption.id, ref, false),
        )}
      </>
    )
  }

  return (
    <>
      <Typography
        variant='overline'
        className='filter-section-header'
        sx={{
          lineHeight: '2',
          color: 'text.secondary',
        }}
      >
        {category.label}
      </Typography>
      {!category.hideSearch &&
        (category.options?.length ?? 0) + (category.noneOption ? 1 : 0) >= SEARCH_THRESHOLD && (
          <Box
            className='filter-search-wrapper filter-search-wrapper--submenu'
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Search
              placeholder={`Search ${category.label.toLowerCase()}…`}
              value={submenuSearch}
              onChange={(e) => setSubmenuSearch(e.target.value)}
              onClear={() => setSubmenuSearch('')}
            />
          </Box>
        )}
      {category.noneOption && !submenuSearch.trim() && (
        <MenuItem
          className='filter-menu-item'
          onClick={() => handleOptionSelect(category.noneOption!.id)}
        >
          <Box className='filter-item-content'>
            <Checkbox
              size='small'
              checked={isOptionSelected(category.noneOption!.id)}
              className='filter-checkbox'
              tabIndex={-1}
              disableRipple
            />
            <Typography
              variant='body2'
              noWrap
              className='filter-item-label--none'
              sx={{
                color: 'text.secondary',
              }}
            >
              {category.noneOption.label}
            </Typography>
          </Box>
        </MenuItem>
      )}
      {category.options
        ?.filter((opt) =>
          submenuSearch.trim()
            ? (opt.searchLabel ?? opt.label).toLowerCase().includes(submenuSearch.toLowerCase())
            : true,
        )
        .map((opt) => (
          <React.Fragment key={opt.id}>
            <Tooltip title={opt.disabled ? (opt.tooltip ?? '') : ''} placement='right'>
              <span>
                <MenuItem
                  className='filter-menu-item'
                  disabled={opt.disabled}
                  onClick={() => handleOptionSelect(opt.id)}
                >
                  <Box className='filter-item-content'>
                    <Checkbox
                      size='small'
                      checked={isOptionSelected(opt.id)}
                      className='filter-checkbox'
                      tabIndex={-1}
                      disableRipple
                    />
                    {opt.avatar}
                    {opt.icon && (
                      <Icon
                        name={opt.icon}
                        size={typography.fontSize.lg}
                        color={opt.iconColor ?? lightTokens.text.primary}
                      />
                    )}
                    {opt.node ? (
                      opt.node
                    ) : (
                      <Typography variant='body2' noWrap>
                        {opt.label}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              </span>
            </Tooltip>
            {isOptionSelected(opt.id) &&
              opt.refinements &&
              opt.refinements.length > 0 &&
              opt.refinements.map((ref) => renderRefinementMenuItem(opt.id, ref, true))}
          </React.Fragment>
        ))}
      {category.emptyLabel && !category.options?.length && !category.drilldowns?.length && (
        <MenuItem disabled className='filter-menu-item'>
          <InternalTypography variant='body2'>{category.emptyLabel}</InternalTypography>
        </MenuItem>
      )}
      {category.drilldowns && category.drilldowns.length > 0 && (
        <>
          {(category.options?.length ?? 0) > 0 && (
            <Divider className='filter-divider filter-divider--spacing-y' />
          )}
          {category.drilldowns.map((dd) => (
            <MenuItem
              key={dd.id}
              className='filter-menu-item'
              onClick={() => setActiveDrilldown({ drilldown: dd })}
            >
              <Box className='filter-item-content'>
                {dd.icon && (
                  <Icon
                    name={dd.icon}
                    size={typography.fontSize.lg}
                    color={lightTokens.text.secondary}
                  />
                )}
                <Typography variant='body2' noWrap>
                  {dd.label}
                </Typography>
              </Box>
              {getDrilldownCount(dd) > 0 && (
                <Box className='filter-badge'>{getDrilldownCount(dd)}</Box>
              )}
              <Icon
                name='ArrowForward'
                size={typography.fontSize.lg}
                color={lightTokens.text.secondary}
              />
            </MenuItem>
          ))}
        </>
      )}
      {category.showReset && (
        <>
          <Divider className='filter-divider' />
          <Button
            variant='text'
            fullWidth
            className='filter-reset-button'
            onClick={() => {
              const s = state ?? emptyFilterState
              const nextSelections = { ...s.selections }
              delete nextSelections[category.id]
              const nextRefinements = { ...s.refinements }
              delete nextRefinements[category.id]
              const nextAF = { ...s.answerFilters }
              delete nextAF[category.id]
              onChange({
                ...s,
                selections: nextSelections,
                refinements: nextRefinements,
                answerFilters: nextAF,
              })
            }}
          >
            Reset Filter
          </Button>
        </>
      )}
    </>
  )
}

export default FilterCategoryPanel
